import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import APICall from "../../APICall";
import EndPoints from "../../APICall/EndPoints";
import DashboardCommonTable from "../../components/DashboardCommonTable";
import {
  Col,
  notification,
  Button,
  Popconfirm,
  Space,
  Modal,
  Form,
  Input,
} from "antd";
import { valanBillDate } from "../../components/CommanFuction/CommanFunction";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";

const AlertTable = React.memo(() => {
  const queryClient = useQueryClient();
  const isStale = queryClient.getQueryCache().find(["alert"])?.isStale();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [form] = Form.useForm();

  // life cycle
  useEffect(() => {
    if (isStale) {
      queryClient.invalidateQueries({ queryKey: ["alert"] });
    }
  }, []);

  // api call
  const getAlert = async ({ pageParam = 1 }) => {
    if (!pageParam) {
      return;
    }
    let response = await APICall(
      "get",
      `${EndPoints.getAlertNotification}?page=${pageParam}&limit=10`
    );
    return response?.data?.data;
  };

  const alertQuery = useInfiniteQuery({
    queryKey: ["alert"],
    queryFn: getAlert,
    getNextPageParam: (lastPage) => lastPage?.nextPage,
  });

  if (alertQuery?.error) {
    notification.error({ message: alertQuery?.error?.message });
  }

  const onEdit = (record) => {
    setSelectedRecord(record);
    form.setFieldsValue(record); // pre-fill form fields
    setIsModalVisible(true);
  };

  const onDelete = async (record) => {
    console.log("Delete clicked", record);
    await APICall("delete", `${EndPoints.deleteAlert}/${record._id}`);

    notification.success({ message: "Alert deleted successfully" });
    queryClient.invalidateQueries({ queryKey: ["alert"] });
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const updatedRecord = { ...selectedRecord, ...values };

      await APICall(
        "put",
        `${EndPoints.updateAlert}/${updatedRecord._id}`,
        updatedRecord
      );

      setIsModalVisible(false);
      notification.success({ message: "Alert updated successfully" });

      queryClient.invalidateQueries({ queryKey: ["alert"] });
    } catch (err) {
      console.error("Validation error", err);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const allData =
    alertQuery?.data?.pages?.flatMap((el) => el?.notifications) || [];

  const alertColumns = [
    {
      title: "TIME",
      dataIndex: "time",
      key: "time",
      width: "10rem",
      align: "left",
      render: (text, record) => {
        return <span>{valanBillDate(record?.createdAt, true)}</span>;
      },
    },
    {
      title: "TYPE",
      dataIndex: "type",
      key: "type",
      width: "4rem",
      align: "left",
      render: (text, record) => {
        return <span>{record?.type}</span>;
      },
    },
    {
      title: "MESSAGE",
      dataIndex: "message",
      key: "message",
      align: "left",
      render: (text, record) => {
        return <span>{record?.message}</span>;
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      align: "left",
      render: (text, record) => {
        return (
          <Space>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
            />
            <Popconfirm
              title="Are you sure to delete this alert?"
              onConfirm={() => onDelete(record)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="text" icon={<DeleteOutlined />} danger />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <Col span={24}>
      <DashboardCommonTable
        columns={alertColumns}
        dataSource={allData?.[0] ? allData : []}
        tableTitle="ALERT"
        queryKey="alert"
        isLoading={alertQuery?.isLoading}
        tradeQuery={alertQuery}
        viewMore={true}
        allowBtn={false}
      />

      <Modal
        title="Edit Alert"
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="Save"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="message"
            label="Message"
            rules={[{ required: true, message: "Please enter a message" }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </Col>
  );
});

export default AlertTable;
